import { FC, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { CounterpartyForm } from "../../tradingCounterparties/counterpartyForm";
import { BrokerForm } from "../../brokers/brokerForm";
import { useCreateCounterparty } from "../../tradingCounterparties/dataHooks/counterpartyQueries";
import { useCreateBroker } from "../../brokers/dataHooks/brokerQueries";
import { counterpartyAttachmentApi } from "../../tradingCounterparties/dataHooks/attachmentQueries";
import { brokerAttachmentApi } from "../../brokers/dataHooks/attachmentQueries";
import {
  EntityKind,
  entityBasePath,
  MERGERS_PATH
} from "../../../utils/entityPaths";
import {
  answersComplete,
  MergerAnswers,
  pickAnswers,
  toLifecycleAnswers
} from "../marketAccessFields";
import { MergerSection } from "../mergerSection";
import { MERGER_ATTACHMENT_FOLDER } from "../mergerAttachments";
import { PendingFiles } from "./pendingFiles";

/**
 * Onboards a merger. The type picked first decides everything after it: which form, which
 * Market Access answers, which create endpoint, which lifecycles the merger then runs through.
 * Mergers are created here only, never requested.
 */
export const NewMerger: FC = () => {
  const navigate = useNavigate();
  const close = () => navigate(MERGERS_PATH);

  const [kind, setKind] = useState<EntityKind | null>(null);
  const [description, setDescription] = useState("");
  const [answers, setAnswers] = useState<MergerAnswers>({});
  const [files, setFiles] = useState<File[]>([]);
  const [uploadFailure, setUploadFailure] = useState<{
    path: string;
    failed: string[];
  } | null>(null);
  const [uploading, setUploading] = useState(false);

  const createCounterparty = useCreateCounterparty();
  const createBroker = useCreateBroker();
  // Hooks, so both are called; the one for the picked kind is used.
  const uploadCounterparty = counterpartyAttachmentApi.useUpload();
  const uploadBroker = brokerAttachmentApi.useUpload();

  const pickKind = (next: EntityKind | null) => {
    if (!next) return;
    setKind(next);
    // Keep only answers this type has, so a broker can never be saved with e.g. collateral.
    setAnswers((current) => pickAnswers(next, current));
  };

  const finish = async (created: string | undefined, picked: EntityKind) => {
    if (!created) {
      close();
      return;
    }
    const path = `${entityBasePath(picked, true)}/${created}`;
    const upload =
      picked === "counterparty" ? uploadCounterparty : uploadBroker;
    const failed: string[] = [];
    setUploading(true);
    for (const file of files) {
      try {
        await upload.mutateAsync({
          entityId: created,
          lifecycleName: MERGER_ATTACHMENT_FOLDER,
          file
        });
      } catch {
        failed.push(file.name);
      }
    }
    setUploading(false);
    if (failed.length) {
      // The merger exists either way; say which documents still need adding.
      setUploadFailure({ path, failed });
      return;
    }
    navigate(path);
  };

  const mergerFields = (picked: EntityKind) => ({
    isMerger: true,
    mergerDescription: description.trim(),
    marketAccessLifecycle: toLifecycleAnswers(picked, answers)
  });

  const mergerIncomplete =
    !kind || !description.trim() || !answersComplete(kind, answers);
  const submitting =
    createCounterparty.isPending || createBroker.isPending || uploading;
  const createError = createCounterparty.isError || createBroker.isError;

  const extraSection = kind && (
    <MergerSection
      kind={kind}
      description={description}
      onDescriptionChange={setDescription}
      answers={answers}
      onAnswerChange={(key, value) =>
        setAnswers((current) => ({ ...current, [key]: value }))
      }
      attachments={<PendingFiles files={files} onChange={setFiles} />}
    />
  );

  return (
    <Dialog maxWidth="md" fullWidth open onClose={close}>
      <DialogTitle>
        <Typography variant="h5" component="span">
          Onboard New Merger
        </Typography>
      </DialogTitle>
      <DialogContent>
        {uploadFailure ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              paddingTop: 1
            }}
          >
            <Alert severity="warning">
              The merger was created, but these documents did not upload:{" "}
              {uploadFailure.failed.join(", ")}. Add them from Edit merger on
              the merger&apos;s page.
            </Alert>
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                onClick={() => navigate(uploadFailure.path)}
              >
                Open merger
              </Button>
            </Box>
          </Box>
        ) : (
          <>
            <Box sx={{ paddingTop: 1 }}>
              <ToggleButtonGroup
                exclusive
                color="primary"
                value={kind}
                onChange={(_e, next: EntityKind | null) => pickKind(next)}
              >
                <ToggleButton value="counterparty">
                  Trading counterparty
                </ToggleButton>
                <ToggleButton value="broker">Broker</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {createError && (
              <Alert severity="error" sx={{ marginTop: 2 }}>
                We couldn&apos;t create the merger. Please try again.
              </Alert>
            )}

            {kind === "counterparty" && (
              <CounterpartyForm
                extraSection={extraSection}
                submitDisabled={mergerIncomplete}
                submitting={submitting}
                submitLabel="Create merger"
                onCancel={close}
                onSubmit={async (request) => {
                  let id: string;
                  try {
                    id = await createCounterparty.mutateAsync({
                      ...request,
                      ...mergerFields("counterparty")
                    });
                  } catch {
                    return; // shown through createError
                  }
                  await finish(id, "counterparty");
                }}
              />
            )}
            {kind === "broker" && (
              <BrokerForm
                extraSection={extraSection}
                submitDisabled={mergerIncomplete}
                submitting={submitting}
                submitLabel="Create merger"
                onCancel={close}
                onSubmit={async (request) => {
                  let id: string;
                  try {
                    id = await createBroker.mutateAsync({
                      ...request,
                      ...mergerFields("broker")
                    });
                  } catch {
                    return; // shown through createError
                  }
                  await finish(id, "broker");
                }}
              />
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
